import { Button, Skeleton } from "@autonoma/blacklight";
import { BroadcastIcon } from "@phosphor-icons/react/Broadcast";
import { PencilSimpleIcon } from "@phosphor-icons/react/PencilSimple";
import { SlidersHorizontalIcon } from "@phosphor-icons/react/SlidersHorizontal";
import { SparkleIcon } from "@phosphor-icons/react/Sparkle";
import { createFileRoute, Link, notFound, Outlet, redirect } from "@tanstack/react-router";
import { ensureBranchData } from "lib/query/branches.queries";
import { Suspense } from "react";
import { useIsMainBranchSelected, useSelectedBranch } from "../-use-selected-branch";
import { AppLink } from "../../-app-link";
import { BranchPicker } from "./-branch-picker";
import { TestsTreeProvider } from "./-tests-tree/tests-tree-context";
import { TestsTreePanel } from "./-tests-tree/tests-tree-panel";
import { TestChangesContext, useBranchTestChanges } from "./-use-test-changes";

export const Route = createFileRoute("/_blacklight/_app-shell/app/$appSlug/tests")({
  validateSearch: (search: Record<string, unknown>): { branch?: string } => ({
    branch: typeof search.branch === "string" ? search.branch : undefined,
  }),
  loaderDeps: ({ search }) => ({ branch: search.branch }),
  // Prefetch the selected branch so switching branches (even with no test open) doesn't blank the page while
  // useSelectedBranch resolves - TanStack keeps the previous content until the branch data is cached.
  loader: async ({ context, params: { appSlug }, deps: { branch } }) => {
    const app = context.applications.find((a: { slug: string }) => a.slug === appSlug);
    if (app == null) throw notFound();
    try {
      await ensureBranchData(context.queryClient, app.id, branch ?? app.mainBranch.name);
    } catch (error) {
      // A stale or invalid `?branch=` (deleted/renamed branch) should degrade to main, not the route error
      // boundary. Only the explicit param can be bad here - a failing main branch is a genuine error, so rethrow.
      if (branch == null) throw error;
      console.warn("Unknown branch in ?branch=, falling back to main", { branch, error });
      throw redirect({ to: "/app/$appSlug/tests", params: { appSlug }, search: { branch: undefined } });
    }
  },
  // `TestsPage` reads the selected branch at its top with no boundary of its own, so both the loader's wait
  // and that read land here. The title is static; the branch picker and counts are not.
  pendingComponent: TestsPending,
  component: TestsPage,
});

function TestsPending() {
  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-medium tracking-tight text-text-primary">Tests</h1>
            <Skeleton className="h-8 w-32" />
          </div>
          <Skeleton className="mt-2 h-3 w-40" />
        </div>
      </header>

      <div className="flex min-h-0 flex-1 gap-4">
        <div className="w-72 shrink-0 overflow-hidden">
          <div className="h-full border border-border-mid bg-surface-raised">
            <TreePanelSkeleton />
          </div>
        </div>
        <div className="min-w-0 flex-1 overflow-hidden border border-border-mid bg-surface-raised" />
      </div>
    </div>
  );
}

function TreePanelSkeleton() {
  return (
    <div className="flex flex-col gap-2 p-3">
      <Skeleton className="h-4 w-32" />
      <Skeleton className="ml-4 h-4 w-24" />
      <Skeleton className="ml-4 h-4 w-28" />
      <Skeleton className="ml-4 h-4 w-20" />
    </div>
  );
}

function BranchMeta({
  testCount,
  addedCount,
  modifiedCount,
}: {
  testCount: number;
  addedCount: number;
  modifiedCount: number;
}) {
  const changeParts: string[] = [];
  if (addedCount > 0) changeParts.push(`${addedCount} new`);
  if (modifiedCount > 0) changeParts.push(`${modifiedCount} modified`);

  return (
    <p className="mt-2 flex items-center gap-2 font-mono text-xs text-text-secondary">
      <span>
        {testCount} {testCount === 1 ? "test" : "tests"}
      </span>
      {changeParts.length > 0 && (
        <>
          <span>·</span>
          <span className="flex items-center gap-1.5 text-text-primary">
            <span className="size-1.5 shrink-0 bg-primary" />
            {changeParts.join(" · ")} on this branch
          </span>
        </>
      )}
    </p>
  );
}

function TestsPage() {
  const { appSlug } = Route.useParams();
  const branch = useSelectedBranch();
  const isMain = useIsMainBranchSelected();
  const testCount = branch.activeSnapshot.testCaseAssignments.length;
  const hasPending = branch.pendingSnapshotId != null;

  const changes = useBranchTestChanges(branch.id, !isMain);

  const header = (
    <header className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-medium tracking-tight text-text-primary">Tests</h1>
          <BranchPicker />
        </div>
        <BranchMeta testCount={testCount} addedCount={changes.addedCount} modifiedCount={changes.modifiedCount} />
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {/* Three entry points, all of them things that act ON this suite rather than places to go: where the
            tests came from, the instructions the authoring agent follows, and the scenarios a test can be
            pinned to. The last two live in Settings, which is nowhere near where they take effect. */}
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 font-mono text-2xs text-text-secondary"
          render={<AppLink to="/app/$appSlug/generation-progress" />}
        >
          <SparkleIcon size={12} />
          Generations
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 font-mono text-2xs text-text-secondary"
          render={<AppLink to="/app/$appSlug/settings" />}
        >
          <SlidersHorizontalIcon size={12} />
          Agent instructions
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 font-mono text-2xs text-text-secondary"
          render={<AppLink to="/app/$appSlug/settings/scenarios" />}
        >
          <BroadcastIcon size={12} />
          Scenarios
        </Button>
        <Button
          size="sm"
          className="gap-1.5 font-mono text-2xs"
          render={<Link to="/app/$appSlug/edit" params={{ appSlug }} search={{ branch: branch.name }} />}
        >
          <PencilSimpleIcon size={12} />
          {hasPending ? "Open editor" : "Edit test suite"}
        </Button>
      </div>
    </header>
  );

  return (
    <TestChangesContext.Provider value={changes}>
      <TestsTreeProvider>
        {/* `h-full` is what makes the rest of this layout mean anything: the `min-h-0 flex-1` below and the
            panels' own `h-full` resolve against it, so the tree and the open test scroll inside their boxes
            and the heading stays put. Without it the panels sized to their content and the whole page
            scrolled, taking the branch picker and the test count with it. */}
        <div className="flex h-full flex-col gap-6 overflow-hidden">
          <div className="shrink-0">{header}</div>

          <div className="flex min-h-0 flex-1 gap-4">
            <div className="w-72 shrink-0 overflow-hidden">
              <div className="h-full border border-border-mid bg-surface-raised">
                <Suspense fallback={<TreePanelSkeleton />}>
                  <TestsTreePanel />
                </Suspense>
              </div>
            </div>

            <div className="min-w-0 flex-1 overflow-hidden border border-border-mid bg-surface-raised">
              <Outlet />
            </div>
          </div>
        </div>
      </TestsTreeProvider>
    </TestChangesContext.Provider>
  );
}
